import {System} from "../../../../models/systems/system/system";
import {Group, SystemGroup} from "../../../../models/users/group/group";
import {
    DEFAULT_GROUP_NAMES_BY_SYSTEM,
    SYSTEM_IDS_BY_ACCOUNT_TYPE,
} from "./system.accesses.config";
import {SystemAccessFormValue} from "./system.accesses.types";

export type GroupsBySystemId = ReadonlyMap<string, Group[]>;

export const normalizeGroupName = (name: string): string => name
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

export const createGroupsBySystemId = (systemGroups: SystemGroup[]): GroupsBySystemId =>
    new Map(systemGroups.map(({id, groups}) => [id, groups]));

export const createAccessesBySystemId = (
    accesses: SystemAccessFormValue[]
): ReadonlyMap<string, SystemAccessFormValue> =>
    new Map(accesses.map((access) => [access.system, access]));

export const getGroupsForSystem = (systemId: string, groupsBySystemId: GroupsBySystemId): Group[] =>
    groupsBySystemId.get(systemId) ?? [];

export const getDefaultGroup = (
    systemId: string,
    groupsBySystemId: GroupsBySystemId
): Group | undefined => {
    const defaultGroupName = DEFAULT_GROUP_NAMES_BY_SYSTEM[systemId];
    if (!defaultGroupName) {
        return undefined;
    }

    const normalizedDefaultGroupName = normalizeGroupName(defaultGroupName);
    return getGroupsForSystem(systemId, groupsBySystemId)
        .find(({name}) => normalizeGroupName(name) === normalizedDefaultGroupName);
};

export const resolveDefaultGroups = (
    accesses: SystemAccessFormValue[],
    groupsBySystemId: GroupsBySystemId
): SystemAccessFormValue[] => {
    let changed = false;

    const resolvedAccesses = accesses.map((access) => {
        if (access.group !== undefined && access.group !== null) {
            return access;
        }

        const defaultGroup = getDefaultGroup(access.system, groupsBySystemId);
        if (!defaultGroup) {
            return access;
        }

        changed = true;
        return {...access, group: defaultGroup.id};
    });

    return changed ? resolvedAccesses : accesses;
};

export const filterSystemsByAccountType = (systems: System[], type: string): System[] => {
    const allowedSystemIds = new Set(SYSTEM_IDS_BY_ACCOUNT_TYPE[type] ?? []);
    return systems.filter(({id}) => allowedSystemIds.has(id));
};

export const hasMissingGroup = (accesses: SystemAccessFormValue[]): boolean =>
    accesses.some(({group}) => group === undefined || group === null);
